import { useFeatureFlags } from '@fs/zion-flags'
import { ContentAttach, NoticeLoading } from '@fs/zion-icon'
import { useSlsStateContext } from '@fs/zion-sls'
import {
  Alert,
  Button,
  colors,
  QuickGlanceActions,
  QuickGlanceOverlay,
  H2,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  Paragraph,
  Row,
  Separator,
  useOverlay,
} from '@fs/zion-ui'
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { errorCollector } from '../../../../../shared/utils/utils'
import hasMeaningfulContent from '../../utils/hasMeaningfulContent'
import useGenealogistQuestion from '../../hooks/useGenealogistQuestion'
import PersonElement from './PersonElement'
import {
  createPersonExtractionRequest,
  createRelationshipLinkingRequest,
  extractAndParseJson,
} from './AIPersonsExpander.utils'

// isTranscriptReady is false while the new image's transcript loads; plainTextTranscript can then still be the
// previous image's text, so no request is sent until it is ready.
const AIPersonsExpander = ({ plainTextTranscript, isTranscriptReady = true, transcriptError = false }) => {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const overlay = useOverlay()

  // Initialize the genealogist question hooks
  // One question will be asked right after the other
  const {
    loading: loadingPersonExtraction,
    answer: personExtractionAnswer,
    error: personExtractionError,
    askQuestion: extractPersons,
  } = useGenealogistQuestion()

  const {
    loading: loadingRelationshipLinking,
    answer: relationshipLinkingAnswer,
    error: relationshipLinkingError,
    askQuestion: linkRelationships,
  } = useGenealogistQuestion()

  const { rip_sharedImageViewer_ShowPromptDetails: socialStarFlag } = useFeatureFlags([
    'rip_sharedImageViewer_ShowPromptDetails',
  ])

  const currentImageArk = useMemo(() => state.currentImageArk, [state.currentImageArk])

  const [aiGeneratedPersons, setAiGeneratedPersons] = useState([])
  const [isAiPersonsExpanderOpen, setIsAiPersonsExpanderOpen] = useState(false)
  const [summaryError, setSummaryError] = useState(false)
  const [summarization] = useState('Here is the response we get from OpenAI')

  // Determine if any AI operation is loading, or the section is open and waiting for the transcript
  const waitingForTranscript = isAiPersonsExpanderOpen && !isTranscriptReady && !transcriptError
  const loadingAiPersons = loadingPersonExtraction || loadingRelationshipLinking || waitingForTranscript

  const handleSummarizeClick = (e) => {
    e.stopPropagation()
    if (socialStarFlag.isOn) {
      overlay.open({ target: e.currentTarget })
    }
  }

  const fetchAiPersons = useCallback(async () => {
    try {
      if (!isTranscriptReady) {
        // The transcript failed to load, so it will never be ready: show the error instead of waiting
        if (transcriptError) setSummaryError(true)
        return
      }
      const noMeaningfulContent = !hasMeaningfulContent(plainTextTranscript)
      const incompleteData = !plainTextTranscript || !currentImageArk
      if (noMeaningfulContent || incompleteData) {
        setAiGeneratedPersons([])
        return
      }

      // First call for person extraction
      const personExtractionRequest = createPersonExtractionRequest(plainTextTranscript)
      await extractPersons(personExtractionRequest)
    } catch (error) {
      setSummaryError(true)
      errorCollector(error, currentImageArk, {
        codeId: 30,
      })
    }
  }, [extractPersons, plainTextTranscript, currentImageArk, isTranscriptReady, transcriptError])

  // An answer came back but can't be read (empty, cut off, or no JSON): show the error, not an empty list
  const reportUnreadableAnswer = useCallback(() => {
    setSummaryError(true)
    errorCollector(new Error('AI persons answer could not be read'), currentImageArk, {
      codeId: 30,
    })
  }, [currentImageArk])

  // Handle person extraction response. The answer is a string once a request finishes, even an empty one.
  useEffect(() => {
    if (typeof personExtractionAnswer !== 'string' || loadingPersonExtraction) return
    const parsedData = extractAndParseJson(personExtractionAnswer)

    if (parsedData) {
      // Second call for relationship linking
      const relationshipRequest = createRelationshipLinkingRequest(plainTextTranscript, parsedData)
      linkRelationships(relationshipRequest)
    } else {
      reportUnreadableAnswer()
    }
  }, [personExtractionAnswer, loadingPersonExtraction, plainTextTranscript, linkRelationships, reportUnreadableAnswer])

  // Handle relationship linking response
  useEffect(() => {
    if (typeof relationshipLinkingAnswer !== 'string' || loadingRelationshipLinking) return
    const finalParsedData = extractAndParseJson(relationshipLinkingAnswer)

    if (finalParsedData) {
      setAiGeneratedPersons(finalParsedData)
    } else {
      reportUnreadableAnswer()
    }
  }, [relationshipLinkingAnswer, loadingRelationshipLinking, reportUnreadableAnswer])

  // Handle errors
  useEffect(() => {
    const error = personExtractionError || relationshipLinkingError
    if (!error) return
    setSummaryError(true)
    errorCollector(error, currentImageArk, {
      codeId: 30,
    })
  }, [personExtractionError, relationshipLinkingError, currentImageArk])

  useEffect(() => {
    if (!isAiPersonsExpanderOpen) return
    fetchAiPersons()
  }, [fetchAiPersons, isAiPersonsExpanderOpen])

  const retryFetch = () => {
    setSummaryError(false)
    fetchAiPersons()
  }

  return (
    <Expander
      open={isAiPersonsExpanderOpen && currentImageArk === state.currentImageArk}
      setOpen={(value) => setIsAiPersonsExpanderOpen(value)}
      linkName="full-text-summarize-expander"
    >
      <ExpanderHeader>
        <Separator size="nano" />
        <Row alignItemsY="middle" guttersY="lg">
          <ContentAttach color={colors.blue60} onClick={handleSummarizeClick} />
          <strong style={{ color: colors.blue60 }}>{t('full-text.ai-records-header', 'Connect people to tree')}</strong>
        </Row>
        <Separator size="nano" />
      </ExpanderHeader>
      <QuickGlanceOverlay {...overlay} title={t('full-text.prompt-title', 'Prompt sent to ChatGPT')}>
        <Paragraph size="sm">{summarization}</Paragraph>
        <QuickGlanceActions>
          <Button
            onClick={overlay.close}
            keyline="end"
            data-testid="close-quick-glance-button"
            linkName="close-quick-glance"
          >
            {t('full-text.close-button', 'Close')}
          </Button>
        </QuickGlanceActions>
      </QuickGlanceOverlay>
      <ExpanderContent>
        {loadingAiPersons ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <NoticeLoading />
          </div>
        ) : (
          <>
            {aiGeneratedPersons && aiGeneratedPersons.length > 0 && (
              <>
                <H2 size="H5">{t('full-text.ai-generated-persons', 'AI-Generated Persons')}</H2>
                <Separator size="xs" />
                <PersonElement persons={aiGeneratedPersons} />
                <Separator size="xs" />
              </>
            )}
            {summaryError && (
              <>
                <Alert type="warning">
                  {t(
                    'full-text.summary-error',
                    'We encountered an error creating the summary. Please try again later.'
                  )}
                </Alert>
                <Separator size="xs" />
                <Button emphasis="medium" onClick={retryFetch}>
                  {t('full-text.summary-error-retry', 'Try Again')}
                </Button>
              </>
            )}
          </>
        )}
        <Separator size="xs" />
      </ExpanderContent>
    </Expander>
  )
}

// A new image gets a new AIPersonsExpander, so nothing from the previous image (its open section, its people, or an
// answer still on the way) can show on this one. Unmounting also aborts that image's requests (useGenealogistQuestion).
const AIPersonsExpanderForImage = (props) => {
  const state = useSlsStateContext()
  return <AIPersonsExpander key={state.currentImageArk} {...props} />
}

export default AIPersonsExpanderForImage
